import type { ExperienceCue, ExperienceManifest, ThoughtIdentityConfig } from './content/types';
import { activeChapterAt } from './content/types';
import { MediaDirector, type MediaSnapshot } from './media/MediaDirector';
import { CaptionDirector } from './media/CaptionDirector';
import { ClinicalWorld, type WorldMode } from './world/ClinicalWorld';

export class TinesExperience {
  private manifest!: ExperienceManifest;
  private readonly video: HTMLVideoElement;
  private readonly assessmentVideo = document.createElement('video');
  private readonly media: MediaDirector;
  private readonly subtitles: CaptionDirector;
  private readonly world: ClinicalWorld;
  private animationFrame = 0;
  private lastFrame = performance.now() / 1000;
  private startedAt = this.lastFrame;
  private lastMediaSnapshot?: MediaSnapshot;
  private captionsOpen = false;
  private statusTimer = 0;
  private idleTimer = 0;
  private fps = 0;
  private fpsFrames = 0;
  private fpsWindow = this.lastFrame;
  private disposed = false;
  private assessmentSource: string | null = null;
  private transcriptReturnFocus: HTMLElement | null = null;
  private readonly reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  private readonly ui = {
    startPanel: byId<HTMLElement>('start-panel'),
    enterMorning: byId<HTMLButtonElement>('enter-morning'),
    previewFuture: byId<HTMLButtonElement>('preview-future'),
    futurePanel: byId<HTMLElement>('future-panel'),
    futureReturn: byId<HTMLButtonElement>('future-return'),
    thought: byId<HTMLElement>('thought-overlay'),
    thoughtRole: byId<HTMLElement>('thought-role'),
    thoughtTitle: byId<HTMLElement>('thought-title'),
    thoughtBody: byId<HTMLElement>('thought-body'),
    assessment: byId<HTMLElement>('assessment-overlay'),
    controls: byId<HTMLElement>('experience-controls'),
    timeline: byId<HTMLInputElement>('timeline'),
    cueMarkers: byId<HTMLElement>('cue-markers'),
    currentTime: byId<HTMLElement>('current-time'),
    duration: byId<HTMLElement>('duration'),
    chapter: byId<HTMLElement>('chapter-label'),
    beat: byId<HTMLElement>('beat-label'),
    playPause: byId<HTMLButtonElement>('play-pause'),
    previousCue: byId<HTMLButtonElement>('previous-cue'),
    nextCue: byId<HTMLButtonElement>('next-cue'),
    returnVestibule: byId<HTMLButtonElement>('return-vestibule'),
    recenter: byId<HTMLButtonElement>('recenter'),
    captions: byId<HTMLButtonElement>('captions-button'),
    transcript: byId<HTMLElement>('transcript-panel'),
    transcriptBackdrop: byId<HTMLElement>('transcript-backdrop'),
    transcriptList: byId<HTMLElement>('transcript-list'),
    closeTranscript: byId<HTMLButtonElement>('close-transcript'),
    mute: byId<HTMLButtonElement>('mute-button'),
    thoughtSounds: byId<HTMLButtonElement>('thought-sounds-button'),
    fullscreen: byId<HTMLButtonElement>('fullscreen-button'),
    roomKicker: byId<HTMLElement>('room-kicker'),
    roomTitle: byId<HTMLElement>('room-title'),
    status: byId<HTMLOutputElement>('status'),
    ambientProgress: byId<HTMLElement>('ambient-progress'),
  };

  constructor(private readonly root: HTMLElement, private readonly canvas: HTMLCanvasElement) {
    this.video = byId<HTMLVideoElement>('master-media');
    this.assessmentVideo.muted = true;
    this.assessmentVideo.playsInline = true;
    this.assessmentVideo.preload = 'auto';
    this.assessmentVideo.crossOrigin = 'anonymous';
    this.media = new MediaDirector(this.video);
    this.subtitles = new CaptionDirector(this.video, byId<HTMLButtonElement>('subtitles-button'),
      byId('caption-overlay'), byId('caption-text'), root, message => this.setStatus(message, true));
    this.world = new ClinicalWorld(canvas, this.media.panoramaTexture);
    this.world.setModeListener(this.handleWorldMode);
    this.applyReducedMotion(this.reducedMotionQuery.matches);
    this.ui.enterMorning.disabled = true;
    this.bindControls();
  }

  async start(): Promise<void> {
    try {
      const response = await fetch(`${import.meta.env.BASE_URL}scenarios/morning-experience.json`);
      if (!response.ok) throw new Error(`Scenario manifest unavailable (${response.status}).`);
      this.manifest = validateManifest(await response.json());
      const identityResponse = await fetch(`${import.meta.env.BASE_URL}review/thought-identity.json`);
      if (!identityResponse.ok) throw new Error('Thought identity settings are unavailable.');
      const identity: ThoughtIdentityConfig = await identityResponse.json();
      for (const cue of this.manifest.cues.filter(c => c.kind === 'thought')) {
        if (!identity.anchors[cue.id]) throw new Error(`Missing speaker position for ${cue.id}`);
      }
      this.world.configureThoughtIdentity(identity);
      this.media.configureThoughtIdentity(identity);
      this.renderCueMarkers();
      this.renderTranscript();
      this.ui.timeline.max = String(this.manifest.media.durationSeconds);
      this.ui.duration.textContent = formatTime(this.manifest.media.durationSeconds);
      this.media.onChange(this.handleMediaChange);
      await this.media.load(this.manifest);
      if (this.manifest.media.captions) this.subtitles.load(this.manifest.media.captions.vtt);
      this.ui.enterMorning.disabled = false;
      this.ui.status.textContent = 'Ready';
      this.ui.status.classList.remove('visible');
    } catch (error) {
      this.setStatus(error instanceof Error ? error.message : String(error), true);
    }
    this.animationFrame = requestAnimationFrame(this.tick);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.animationFrame);
    window.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    this.reducedMotionQuery.removeEventListener('change', this.handleReducedMotionChange);
    this.root.removeEventListener('pointermove', this.handleActivity);
    this.root.removeEventListener('pointerdown', this.handleActivity);
    this.root.removeEventListener('focusin', this.handleActivity);
    window.clearTimeout(this.idleTimer);
    window.clearTimeout(this.statusTimer);
    this.setBackgroundInert(false);
    this.media.dispose();
    this.subtitles.dispose();
    this.assessmentVideo.pause();
    this.assessmentVideo.removeAttribute('src');
    this.assessmentVideo.load();
    this.world.dispose();
  }

  private bindControls(): void {
    this.ui.enterMorning.addEventListener('click', () => void this.enterMorning());
    this.ui.previewFuture.addEventListener('click', () => this.world.previewFuture());
    this.ui.futureReturn.addEventListener('click', () => this.world.returnToVestibule());
    this.ui.returnVestibule.addEventListener('click', () => {
      this.media.pause();
      this.world.returnToVestibule();
    });
    this.ui.playPause.addEventListener('click', () => void this.media.toggle().catch(this.showMediaError));
    this.ui.previousCue.addEventListener('click', () => this.media.seekCue(-1));
    this.ui.nextCue.addEventListener('click', () => this.media.seekCue(1));
    this.ui.recenter.addEventListener('click', () => {
      this.world.recenter();
      this.setStatus('View recentered');
    });
    this.ui.timeline.addEventListener('input', () => this.media.seek(Number(this.ui.timeline.value)));
    this.ui.mute.addEventListener('click', () => this.media.setMuted(!this.media.snapshot.muted));
    this.ui.thoughtSounds.addEventListener('click', () => {
      const enabled = !this.media.thoughtFeedback.enabled;
      this.media.thoughtFeedback.setEnabled(enabled);
      this.ui.thoughtSounds.setAttribute('aria-pressed', String(enabled));
      this.ui.thoughtSounds.setAttribute('aria-label', enabled ? 'Turn off thought entrance sounds' : 'Turn on thought entrance sounds');
      this.ui.thoughtSounds.querySelector('.button-label')!.textContent = enabled ? 'Thought sounds on' : 'Thought sounds off';
    });
    this.ui.captions.addEventListener('click', () => this.setTranscriptOpen(!this.captionsOpen));
    this.ui.closeTranscript.addEventListener('click', () => this.setTranscriptOpen(false));
    this.ui.transcriptBackdrop.addEventListener('click', () => this.setTranscriptOpen(false));
    this.ui.fullscreen.addEventListener('click', () => void this.toggleFullscreen().catch(this.showMediaError));
    window.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    this.reducedMotionQuery.addEventListener('change', this.handleReducedMotionChange);
    this.root.addEventListener('pointermove', this.handleActivity);
    this.root.addEventListener('pointerdown', this.handleActivity);
    this.root.addEventListener('focusin', this.handleActivity);
    if (!document.fullscreenEnabled) this.ui.fullscreen.hidden = true;
  }

  private async enterMorning(): Promise<void> {
    this.ui.enterMorning.disabled = true;
    this.world.enterMorning();
    this.setStatus('Entering Room 01…');
    try {
      await this.media.play();
    } catch (error) {
      this.showMediaError(error);
    } finally {
      this.ui.enterMorning.disabled = false;
    }
  }

  private readonly handleWorldMode = (mode: WorldMode): void => {
    this.root.dataset.mode = mode;
    const vestibule = mode === 'vestibule';
    const future = mode === 'future-preview';
    const immersive = mode === 'immersive';
    this.subtitles.setImmersive(immersive);
    this.ui.startPanel.hidden = !vestibule;
    this.ui.futurePanel.hidden = !future;
    this.ui.controls.hidden = !immersive;
    this.ui.roomKicker.textContent = immersive ? 'Room 01 · Live encounter' : future ? 'Room 02 · Preview' : 'Clinic vestibule';
    this.ui.roomTitle.textContent = immersive ? 'Beth + Maria' : future ? 'Scene in development' : 'Choose a perspective';
    if (!immersive) {
      this.ui.thought.hidden = true;
      this.ui.assessment.hidden = true;
    }
    if (immersive) {
      this.updateCue(this.media.snapshot.cue);
      this.canvas.focus();
      this.setStatus('Focused view · Space pauses');
      this.scheduleIdle();
    } else {
      this.root.classList.remove('ui-idle');
      window.clearTimeout(this.idleTimer);
      if (vestibule) this.ui.enterMorning.focus();
      else if (future) this.ui.futureReturn.focus();
    }
  };

  private readonly handleMediaChange = (snapshot: MediaSnapshot): void => {
    this.lastMediaSnapshot = snapshot;
    this.ui.currentTime.textContent = formatTime(snapshot.time);
    this.ui.duration.textContent = formatTime(snapshot.duration || this.manifest.media.durationSeconds);
    if (!this.ui.timeline.matches(':active')) this.ui.timeline.value = String(snapshot.time);
    this.ui.playPause.dataset.playing = String(snapshot.playing);
    this.root.dataset.playing = String(snapshot.playing);
    this.ui.playPause.querySelector('.button-label')!.textContent = this.video.ended ? 'Replay' : snapshot.playing ? 'Pause' : 'Play';
    this.ui.playPause.setAttribute('aria-label', this.video.ended ? 'Replay encounter' : snapshot.playing ? 'Pause' : 'Play');
    this.ui.nextCue.disabled = !this.manifest.cues.some((cue) => cue.start > snapshot.time + 0.25);
    this.ui.mute.setAttribute('aria-pressed', String(snapshot.muted));
    this.ui.mute.setAttribute('aria-label', snapshot.muted ? 'Unmute audio' : 'Mute audio');
    this.ui.mute.classList.toggle('is-active', snapshot.muted);
    this.ui.mute.querySelector('.button-label')!.textContent = snapshot.muted ? 'Unmute' : 'Mute';
    this.ui.mute.title = snapshot.muted ? 'Unmute audio' : 'Mute audio';
    const chapter = activeChapterAt(this.manifest, snapshot.time);
    this.ui.chapter.textContent = chapter.eyebrow;
    this.ui.beat.textContent = snapshot.cue?.title ?? chapter.label;
    this.updateCue(snapshot.cue);
    const duration = snapshot.duration || this.manifest.media.durationSeconds;
    const percent = duration > 0 ? Math.min(100, Math.max(0, (snapshot.time / duration) * 100)) : 0;
    this.root.style.setProperty('--progress', `${percent}%`);
    this.ui.timeline.setAttribute('aria-valuetext', `${formatTime(snapshot.time)} of ${formatTime(duration)}`);
    if (!snapshot.playing) this.wakeInterface();
    this.syncAssessmentInsert(snapshot.cue, snapshot);
    if (snapshot.error) this.setStatus(snapshot.error, true);
  };

  private syncAssessmentInsert(cue: ExperienceCue | null, snapshot: MediaSnapshot): void {
    const assessmentCue = cue?.kind === 'assessment' ? cue : null;
    if (!assessmentCue?.replacementVideo) {
      if (this.assessmentSource) {
        this.assessmentVideo.pause();
        this.world.setAssessmentMedia(null);
        this.assessmentSource = null;
      }
      return;
    }

    const source = assessmentCue.replacementVideo;
    const resolved = `${import.meta.env.BASE_URL}${source}`;
    if (this.assessmentSource !== resolved) {
      this.assessmentSource = resolved;
      this.assessmentVideo.src = resolved;
      this.assessmentVideo.load();
      this.world.setAssessmentMedia(this.assessmentVideo);
    }
    const target = Math.max(0, snapshot.time - assessmentCue.start);
    if (Math.abs(this.assessmentVideo.currentTime - target) > 0.2) this.assessmentVideo.currentTime = target;
    if (snapshot.playing && this.assessmentVideo.paused) void this.assessmentVideo.play().catch(() => undefined);
    if (!snapshot.playing && !this.assessmentVideo.paused) this.assessmentVideo.pause();
  }

  private updateCue(cue: ExperienceCue | null): void {
    this.world.setCue(cue);
    const thought = cue?.kind === 'thought';
    const assessment = cue?.kind === 'assessment';
    const visible = this.world.mode === 'immersive';
    this.ui.thought.hidden = !thought || !visible;
    this.ui.assessment.hidden = !assessment || !visible;
    this.root.classList.toggle('cue-active', Boolean(cue) && visible);
    this.root.classList.toggle('assessment-active', assessment && visible);
    if (!thought || !cue) return;
    const provider = cue.speaker === 'provider';
    this.ui.thought.dataset.speaker = provider ? 'provider' : 'patient';
    this.ui.thoughtRole.textContent = provider ? 'Provider thought · Beth' : 'Patient thought · Maria';
    this.ui.thoughtTitle.textContent = cue.title;
    this.ui.thoughtBody.textContent = cue.summary;
  }

  private renderCueMarkers(): void {
    const duration = this.manifest.media.durationSeconds;
    for (const cue of this.manifest.cues) {
      const marker = document.createElement('i');
      marker.className = `cue-marker ${cue.kind} ${cue.speaker ?? ''}`;
      marker.style.left = `${(cue.start / duration) * 100}%`;
      marker.style.width = `${(((cue.end ?? duration) - cue.start) / duration) * 100}%`;
      marker.title = cue.title;
      this.ui.cueMarkers.append(marker);
    }
  }

  private renderTranscript(): void {
    for (const cue of this.manifest.cues.filter((item) => item.kind === 'thought')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `transcript-entry ${cue.speaker}`;
      button.innerHTML = `<span>${cue.speaker === 'provider' ? 'BETH · PROVIDER' : 'MARIA · PATIENT'} · ${formatTime(cue.start)}</span><strong>${escapeHtml(cue.title)}</strong><em>${escapeHtml(cue.summary)}</em>`;
      button.addEventListener('click', () => {
        this.media.seek(cue.start + 0.05);
        if (this.world.mode !== 'immersive') void this.enterMorning();
        this.setTranscriptOpen(false);
      });
      this.ui.transcriptList.append(button);
    }
  }


  private setTranscriptOpen(open: boolean): void {
    if (open === this.captionsOpen) return;
    if (open) {
      this.transcriptReturnFocus = document.activeElement instanceof HTMLElement
        ? document.activeElement
        : this.ui.captions;
    }
    this.captionsOpen = open;
    this.ui.transcript.hidden = !open;
    this.ui.transcriptBackdrop.hidden = !open;
    this.ui.captions.setAttribute('aria-pressed', String(open));
    this.ui.captions.setAttribute('aria-expanded', String(open));
    this.ui.captions.classList.toggle('is-active', open);
    this.root.classList.toggle('transcript-open', open);
    this.setBackgroundInert(open);
    if (open) {
      this.wakeInterface();
      this.ui.closeTranscript.focus();
    } else {
      this.scheduleIdle();
      const returnFocus = this.transcriptReturnFocus?.isConnected ? this.transcriptReturnFocus : this.ui.captions;
      this.transcriptReturnFocus = null;
      returnFocus.focus();
    }
  }

  private setBackgroundInert(inert: boolean): void {
    for (const child of this.root.children) {
      if (!(child instanceof HTMLElement)) continue;
      if (child === this.ui.transcript || child === this.ui.transcriptBackdrop) continue;
      child.inert = inert;
    }
  }

  private async toggleFullscreen(): Promise<void> {
    if (document.fullscreenElement) await document.exitFullscreen();
    else await this.root.requestFullscreen();
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    this.handleActivity();
    if (this.captionsOpen && event.code === 'Escape') {
      event.preventDefault();
      this.setTranscriptOpen(false);
      return;
    }
    if (this.captionsOpen && event.code === 'Tab') {
      const focusable = [...this.ui.transcript.querySelectorAll<HTMLElement>('button:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first && last && event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (first && last && !event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    if (this.captionsOpen || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLButtonElement ||
      (event.target instanceof HTMLElement && event.target.isContentEditable)) return;
    if (event.code === 'Space' && this.world.mode === 'immersive') {
      event.preventDefault();
      void this.media.toggle().catch(this.showMediaError);
    } else if (event.shiftKey && event.code === 'ArrowLeft') {
      event.preventDefault();
      this.media.seek(this.media.snapshot.time - 5);
    } else if (event.shiftKey && event.code === 'ArrowRight') {
      event.preventDefault();
      this.media.seek(this.media.snapshot.time + 5);
    } else if (event.code === 'KeyC') {
      this.setTranscriptOpen(!this.captionsOpen);
    } else if (event.code === 'KeyM') {
      this.media.setMuted(!this.media.snapshot.muted);
    } else if (event.code === 'KeyR' && this.world.mode === 'immersive') {
      this.world.recenter();
    }
  };

  private readonly tick = (milliseconds: number): void => {
    if (this.disposed) return;
    const now = milliseconds / 1000;
    const delta = Math.min(0.05, Math.max(0, now - this.lastFrame));
    this.lastFrame = now;
    const energy = this.media.getAudioEnergy();
    this.world.update(delta, now, energy, this.media.snapshot.playing);
    this.root.dataset.thoughtSpeaker = this.media.snapshot.cue?.speaker ?? '';
    this.root.dataset.voiceEnergy = energy.toFixed(3);
    this.root.dataset.thoughtSoundCount = String(this.media.thoughtFeedback.announcements);
    this.fpsFrames += 1;
    if (now - this.fpsWindow >= 1) {
      this.fps = this.fpsFrames / (now - this.fpsWindow);
      this.fpsFrames = 0;
      this.fpsWindow = now;
    }
    this.updateDiagnostics(now);
    this.animationFrame = requestAnimationFrame(this.tick);
  };

  private updateDiagnostics(now: number): void {
    const world = this.world.diagnostics() as { renderer: Record<string, number> };
    const snapshot = this.lastMediaSnapshot ?? this.media.snapshot;
    const playback = this.media.getPlaybackQuality();
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.world.frame,
      elapsed: now - this.startedAt,
      state: this.world.mode,
      cueId: snapshot.cue?.id ?? null,
      media: {
        time: snapshot.time,
        duration: snapshot.duration,
        playing: snapshot.playing,
        ready: snapshot.ready,
        droppedFrames: playback.dropped,
        totalFrames: playback.total,
      },
      renderer: world.renderer,
      fps: this.fps,
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: window.devicePixelRatio,
      },
    };
  }

  private setStatus(message: string, error = false): void {
    window.clearTimeout(this.statusTimer);
    this.ui.status.textContent = message;
    this.ui.status.classList.toggle('error', error);
    this.ui.status.classList.add('visible');
    this.statusTimer = window.setTimeout(() => this.ui.status.classList.remove('visible'), error ? 9000 : 1800);
  }

  private readonly handleActivity = (): void => {
    this.wakeInterface();
    this.scheduleIdle();
  };

  private wakeInterface(): void {
    this.root.classList.remove('ui-idle');
    window.clearTimeout(this.idleTimer);
  }

  private scheduleIdle(): void {
    window.clearTimeout(this.idleTimer);
    const snapshot = this.lastMediaSnapshot ?? this.media.snapshot;
    if (this.world.mode !== 'immersive' || !snapshot.playing || this.captionsOpen) return;
    this.idleTimer = window.setTimeout(() => {
      const controlFocused = document.activeElement instanceof HTMLButtonElement || document.activeElement instanceof HTMLInputElement;
      if (!controlFocused && this.world.mode === 'immersive' && this.media.snapshot.playing && !this.captionsOpen) {
        this.root.classList.add('ui-idle');
      }
    }, 3000);
  }

  private readonly showMediaError = (error: unknown): void => {
    this.setStatus(error instanceof Error ? error.message : String(error), true);
  };

  private readonly handleReducedMotionChange = (event: MediaQueryListEvent): void => {
    this.applyReducedMotion(event.matches);
  };

  private applyReducedMotion(reduced: boolean): void {
    this.root.dataset.reducedMotion = String(reduced);
    this.world.setReducedMotion(reduced);
  }

  private readonly handleFullscreenChange = (): void => {
    const fullscreen = document.fullscreenElement === this.root;
    this.ui.fullscreen.setAttribute('aria-pressed', String(fullscreen));
    this.ui.fullscreen.querySelector('.button-label')!.textContent = fullscreen ? 'Exit full' : 'Fullscreen';
    this.ui.fullscreen.setAttribute('aria-label', fullscreen ? 'Exit fullscreen' : 'Fullscreen');
  };
}

function byId<T extends HTMLElement>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing interface element #${id}.`);
  return element as T;
}

function validateManifest(value: unknown): ExperienceManifest {
  if (!value || typeof value !== 'object') throw new Error('Scenario manifest is invalid.');
  const manifest = value as ExperienceManifest;
  if (manifest.schemaVersion !== 3 || !Array.isArray(manifest.cues) || !Array.isArray(manifest.chapters)) throw new Error('Unsupported scenario manifest.');
  let lastStart = -1;
  for (const cue of manifest.cues) {
    if (!cue.id || cue.start < lastStart || (cue.end !== null && cue.end <= cue.start)) throw new Error(`Invalid cue timing near ${cue.id || 'unnamed cue'}.`);
    lastStart = cue.start;
  }
  return manifest;
}

function formatTime(seconds: number): string {
  const value = Math.max(0, Math.floor(Number.isFinite(seconds) ? seconds : 0));
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${String(value % 60).padStart(2, '0')}`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[character]!);
}
